/**
 * Segmented time-period control for Control Centre tabs (Orders, Fuel, …).
 * Parent owns the selected period and date-range math — this is presentation only.
 */

import type { TimePeriod } from '../TimePeriodSelector';
import { Button } from '../ui/button';

/** Periods this chip bar supports today. */
export type ControlCentrePeriodId = Extract<
  TimePeriod,
  'today' | 'yesterday' | 'last-week' | 'last-full-week' | 'month-to-date' | 'last-month'
>;

export const CONTROL_CENTRE_PERIOD_OPTIONS: Array<{ id: ControlCentrePeriodId; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last-week', label: 'Last 7 Days' },
  { id: 'month-to-date', label: 'Month to date' },
  { id: 'last-month', label: 'Last month' },
];

/** Driver tab, walkaround checks and defects: these arrive within minutes, so Today is meaningful. */
export const DRIVER_CHECK_PERIOD_OPTIONS: Array<{ id: ControlCentrePeriodId; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last-week', label: 'Last 7 Days' },
  { id: 'month-to-date', label: 'Month to date' },
  { id: 'last-month', label: 'Last month' },
];

/**
 * Driver tab, drivers' hours and violations. Tacho data is about a day behind
 * (Samsara shows a shift once the driver's next shift starts), so there is no
 * Today; Last full week matches a weekly violations review.
 */
export const DRIVER_HOURS_PERIOD_OPTIONS: Array<{ id: ControlCentrePeriodId; label: string }> = [
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last-full-week', label: 'Last full week' },
  { id: 'month-to-date', label: 'Month to date' },
  { id: 'last-month', label: 'Last month' },
];

export function isControlCentrePeriod(
  period: TimePeriod,
  options: ReadonlyArray<{ id: ControlCentrePeriodId }> = CONTROL_CENTRE_PERIOD_OPTIONS,
): period is ControlCentrePeriodId {
  return options.some((o) => o.id === period);
}

/** Chip-bar periods only — coerce alerts deep-links (`custom`, `last_30_days`, …) to the tab default. */
export function coerceControlCentrePeriod(
  period: TimePeriod | undefined | null,
  fallback: ControlCentrePeriodId,
  options: ReadonlyArray<{ id: ControlCentrePeriodId }> = CONTROL_CENTRE_PERIOD_OPTIONS,
): ControlCentrePeriodId {
  if (period != null && isControlCentrePeriod(period, options)) return period;
  return fallback;
}

type PeriodAccent = 'purple' | 'orange' | 'green' | 'amber';

const ACCENT_SELECTED: Record<PeriodAccent, string> = {
  purple: 'bg-chart-5 text-white font-semibold shadow-sm',
  orange: 'bg-chart-2 text-white font-semibold shadow-sm',
  green: 'bg-chart-3 text-white font-semibold shadow-sm',
  amber: 'bg-chart-2 text-white font-semibold shadow-sm',
};

const ACCENT_FOCUS: Record<PeriodAccent, string> = {
  purple: 'focus-visible:ring-chart-5',
  orange: 'focus-visible:ring-chart-2',
  green: 'focus-visible:ring-chart-3',
  amber: 'focus-visible:ring-chart-2',
};

interface ControlCentrePeriodPanelProps {
  value: TimePeriod;
  onChange: (period: ControlCentrePeriodId) => void;
  /** Override the default chip set (e.g. omit Last month on a tab). */
  options?: Array<{ id: ControlCentrePeriodId; label: string }>;
  /** Orders = purple; Fuel = orange; Drivers = green; Maintenance = amber. */
  accent?: PeriodAccent;
  ariaLabel?: string;
  className?: string;
  /** Periods that render disabled (e.g. Profit “Today” until the day closes). */
  disabledIds?: readonly ControlCentrePeriodId[];
  /** Tooltip / title when a period is disabled. */
  disabledTitle?: (id: ControlCentrePeriodId) => string | undefined;
}

export default function ControlCentrePeriodPanel({
  value,
  onChange,
  options = CONTROL_CENTRE_PERIOD_OPTIONS,
  accent = 'purple',
  ariaLabel = 'Time period',
  className = '',
  disabledIds,
  disabledTitle,
}: ControlCentrePeriodPanelProps) {
  const disabled = new Set(disabledIds ?? []);
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={`inline-flex w-fit max-w-full flex-wrap rounded-lg border border-border bg-background/70 p-1 ${className}`.trim()}
    >
      {options.map((option) => {
        const selected = value === option.id;
        const isDisabled = disabled.has(option.id);
        return (
          <Button
            key={option.id}
            variant="ghost"
            size="sm"
            type="button"
            aria-pressed={selected}
            disabled={isDisabled}
            title={isDisabled ? (disabledTitle?.(option.id) ?? undefined) : undefined}
            onClick={() => {
              if (!isDisabled) onChange(option.id);
            }}
            className={`px-3.5 text-sm rounded-md focus-visible:ring-2 ${ACCENT_FOCUS[accent]} ${
              isDisabled
                ? 'text-muted-foreground'
                : selected
                  ? ACCENT_SELECTED[accent]
                  : 'text-foreground hover:text-foreground hover:bg-secondary/80'
            }`}
          >
            {option.label}
          </Button>
        );
      })}
    </div>
  );
}
